"use client";

import { useEffect } from "react";
import Image from "next/image";
import { applyCSSVariablesToRoot, generateCSSVariables } from "@/lib/theme-utils";
import {
  HeroSection,
  FeaturedCampaignSection,
  CampaignListSection,
  HowItWorksSection,
  CustomTextSection,
} from "./_components";
import type { TenantPageSectionType } from "@/lib/whitelabel-config";

interface Campaign {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  orgName: string | null;
  orgWebsite?: string;
  locationText?: string;
  coverImageUrl?: string;
  status: string;
  startAt: string | null;
  endAt: string | null;
  createdAt: string;
  participantCap?: number;
  mintCap?: number;
  isPublic: boolean;
  participantCount: number;
  clueCount: number;
}

interface Tenant {
  name: string;
  slug: string;
  logoUrl?: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor?: string;
  backgroundColor?: string;
  textColor?: string;
  footerBackgroundColor?: string;
  headerSticky?: boolean;
  headerTransparentInitial?: boolean;
}

interface TenantPageSection {
  id: string;
  sectionType: TenantPageSectionType;
  orderIndex: number;
  title: string | null;
  description: string | null;
  visible: boolean;
}

interface TenantPageClientProps {
  tenant: Tenant;
  campaigns: Campaign[];
  sections?: TenantPageSection[];
}

/**
 * Default sections to use when no custom sections are configured.
 * These mirror the original component layout for backward compatibility.
 */
const DEFAULT_SECTIONS: TenantPageSection[] = [
  { id: 'default-hero', sectionType: 'hero', orderIndex: 0, title: null, description: null, visible: true },
  { id: 'default-featured', sectionType: 'featured-campaigns', orderIndex: 1, title: null, description: null, visible: true },
  { id: 'default-list', sectionType: 'campaign-list', orderIndex: 2, title: null, description: null, visible: true },
  { id: 'default-how', sectionType: 'how-it-works', orderIndex: 3, title: null, description: null, visible: true },
];

export function TenantPageClient({ tenant, campaigns, sections }: TenantPageClientProps) {
  // Apply tenant colors as CSS variables on mount
  useEffect(() => {
    const cssVariables = generateCSSVariables(tenant);
    applyCSSVariablesToRoot(cssVariables);
  }, [tenant]);

  // When header is sticky AND transparent initial is enabled, use negative margin
  // to allow content to extend under the transparent header on the homepage
  const shouldExtendUnderHeader = tenant.headerSticky && tenant.headerTransparentInitial;
  const heroMarginClass = shouldExtendUnderHeader ? '-mt-16 pt-16' : '';

  // Get the featured campaign (most recent active/published)
  const featuredCampaign = campaigns.find(c =>
    c.status === "ACTIVE" || c.status === "PUBLISHED"
  ) || campaigns[0] || null;

  // Use provided sections or fall back to defaults
  const activeSections = sections && sections.length > 0
    ? sections.filter(s => s.visible).sort((a, b) => a.orderIndex - b.orderIndex)
    : DEFAULT_SECTIONS;

  // Ensure campaign-list is always included (core section)
  const hasCampaignList = activeSections.some(s => s.sectionType === 'campaign-list');
  const sectionsToRender = hasCampaignList
    ? activeSections
    : [...activeSections, { id: 'fallback-list', sectionType: 'campaign-list' as TenantPageSectionType, orderIndex: 999, title: null, description: null, visible: true }];

  /**
   * Renders a section based on its type
   */
  const renderSection = (section: TenantPageSection) => {
    switch (section.sectionType) {
      case 'hero':
        return (
          <HeroSection
            key={section.id}
            tenant={tenant}
            title={section.title}
            description={section.description}
          />
        );

      case 'featured-campaigns':
        return featuredCampaign ? (
          <FeaturedCampaignSection
            key={section.id}
            tenant={tenant}
            campaign={featuredCampaign}
            title={section.title}
            description={section.description}
          />
        ) : null;

      case 'campaign-list':
        return (
          <CampaignListSection
            key={section.id}
            tenant={tenant}
            campaigns={campaigns}
            title={section.title}
            description={section.description}
          />
        );

      case 'how-it-works':
        return (
          <HowItWorksSection
            key={section.id}
            title={section.title}
            description={section.description}
          />
        );

      case 'custom-text':
        return (
          <CustomTextSection
            key={section.id}
            tenant={tenant}
            title={section.title}
            description={section.description}
          />
        );

      default:
        return null;
    }
  };

  // Empty state when no campaigns exist
  if (campaigns.length === 0) {
    return (
      <div
        className={`min-h-screen ${heroMarginClass}`}
        style={{ backgroundColor: 'var(--theme-bg, #FFFFFF)' }}
      >
        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="text-center">
            {tenant.logoUrl && (
              <div className="relative h-16 w-48 mx-auto mb-6">
                <Image
                  src={tenant.logoUrl}
                  alt={tenant.name}
                  fill
                  className="object-contain"
                  unoptimized
                />
              </div>
            )}
            <h1
              className="text-4xl font-bold mb-4"
              style={{ color: 'var(--theme-text, #111827)' }}
            >
              {tenant.name}
            </h1>
            <p
              className="text-xl"
              style={{ color: 'var(--theme-text, #6B7280)', opacity: 0.8 }}
            >
              No scavenger hunts available at the moment. Check back soon!
            </p>
          </div>
        </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen ${heroMarginClass}`}
      style={{ backgroundColor: 'var(--theme-bg, #FFFFFF)' }}
    >
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* Render sections in configured order */}
          {sectionsToRender.map(renderSection)}
        </div>
      </div>
    </div>
  );
}
